import { useMemo, useState, useEffect, useCallback } from "react";
import { Assistance } from "@b2bportal/air-booking-api";
import { useI18nContext } from "@hopper-b2b/i18n";

type AssistanceOption = { label: string; value: Assistance };

const getAssistanceOptions = (t): Array<AssistanceOption> => [
  { label: t("assistanceOptions.blind"), value: Assistance.BLND },
  { label: t("assistanceOptions.deaf"), value: Assistance.DEAF },
  { label: t("assistanceOptions.stretcher"), value: Assistance.STCR },
  {
    label: t("assistanceOptions.wheelchairStairs"),
    value: Assistance.WCHR,
  },
  { label: t("assistanceOptions.wheelchairSeat"), value: Assistance.WCHS },
  { label: t("assistanceOptions.wheelchairCarried"), value: Assistance.WCHC },
  {
    label: t("assistanceOptions.wheelchairDryBattery"),
    value: Assistance.WCBD,
  },
  {
    label: t("assistanceOptions.wheelchairWetBattery"),
    value: Assistance.WCBW,
  },
  {
    label: t("assistanceOptions.wheelchairLithiumBattery"),
    value: Assistance.WCLB,
  },
  { label: t("assistanceOptions.wheelchairManual"), value: Assistance.WCMP },
  { label: t("assistanceOptions.wheelchairOnBoard"), value: Assistance.WCOB },
];

interface UseAssistanceSelectionReturn {
  context: {
    label?: string;
    assistanceOptions: Array<AssistanceOption>;
    selectedAssistanceOptions: Array<Assistance>;
    redressModalOpen: boolean;
    ktnModalOpen: boolean;
  };
  handlers: {
    isOptionSelected: (value: Assistance) => boolean;
    onSelectedAssistanceOptionsChange: (value: Assistance[]) => void;
    setRedressModalOpen: (value: boolean) => void;
    setKtnModalOpen: (value: boolean) => void;
  };
}

export const useAssistanceSelection = ({
  formContext,
}): UseAssistanceSelectionReturn => {
  const { t } = useI18nContext();
  const assistanceOptions = getAssistanceOptions(t);
  const [redressModalOpen, setRedressModalOpen] = useState(false);
  const [ktnModalOpen, setKtnModalOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Array<Assistance>>(
    formContext.getValues("assistanceRequired") ?? []
  );

  useEffect(() => {
    formContext.setValue("assistanceRequired", selectedOptions);
  }, [formContext, selectedOptions]);

  const isOptionSelected = useCallback(
    (value: Assistance): boolean => selectedOptions.includes(value),
    [selectedOptions]
  );

  const onSelectedOptionsChange = (value: Assistance[]) => {
    setSelectedOptions(value);
  };

  // Gets the label of the selected assistance options from value
  const label = useMemo(
    () =>
      selectedOptions
        .map((value) => {
          const option = assistanceOptions.find(
            (option) => option.value === value
          );
          return option?.label;
        })
        .join(", "),

    [assistanceOptions, selectedOptions]
  );

  return {
    context: {
      label,
      assistanceOptions,
      selectedAssistanceOptions: selectedOptions,
      redressModalOpen,
      ktnModalOpen,
    },
    handlers: {
      isOptionSelected,
      onSelectedAssistanceOptionsChange: onSelectedOptionsChange,
      setRedressModalOpen,
      setKtnModalOpen,
    },
  };
};
